import { Injectable } from '@angular/core';
import { AppConfigService } from './appConfigService';
import {
  init,
  setUserId,
  identify,
  reset,
  add,
  Identify,
} from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeAnalyticsService {
  private initialized = false;
  private readonly autocaptureOptions = {
    attribution: true,
    fileDownloads: true,
    formInteractions: true,
    pageViews: true,
    sessions: true,
    elementInteractions: true,
    networkTracking: true,
    webVitals: true,
    frustrationInteractions: true,
  };

  constructor(private appConfig: AppConfigService) {}

  private get apiKey(): string {
    return this.appConfig?.config?.DATA?.AMPLITUDE_API_KEY ?? '';
  }

  /**
   * Initialize SDK (post-login) and attach user profile so autocapture events include user context.
   */
  identifyUserFromStorage(): void {
    if (!this.apiKey) {
      return;
    }

    const userJson = localStorage.getItem('currentUser');
    if (!userJson) {
      return;
    }

    let user: Record<string, string>;
    try {
      user = JSON.parse(userJson);
    } catch {
      return;
    }

    const userId = user?.id;
    if (!userId) {
      return;
    }

    this.ensureInitialized(userId);
    this.setUserProfile(user);
  }

  reset(): void {
    if (!this.initialized) {
      return;
    }
    reset();
    this.initialized = false;
  }

  private ensureInitialized(userId: string): void {
    if (!this.initialized) {
      add(engagementPlugin());
      init(this.apiKey, userId, {
        fetchRemoteConfig: true,
        autocapture: this.autocaptureOptions,
      });
      this.initialized = true;
      return;
    }

    setUserId(userId);
  }

  private setUserProfile(user: Record<string, string>): void {
    const identifyEvent = new Identify();
    const firstName = user.firstName ?? '';
    const lastName = user.lastName ?? '';

    identifyEvent.set('id', user.id);
    if (user.loggedInUserEmail) {
      identifyEvent.set('email', user.loggedInUserEmail);
    }
    if (user.officeLocation) {
      identifyEvent.set('office', user.officeLocation);
    }
    if (user.lineOfBusiness_peopleProfile) {
      identifyEvent.set('businessLine', user.lineOfBusiness_peopleProfile);
    }
    if (user.jobTitle) {
      identifyEvent.set('careerLevel', user.jobTitle);
    }
    if (user.userLanguage) {
      identifyEvent.set('language', user.userLanguage);
    }
    if (user.serviceLine) {
      identifyEvent.set('serviceLine', user.serviceLine);
      identifyEvent.set('Account', user.serviceLine);
    }
    if (firstName || lastName) {
      identifyEvent.set('userName', `${firstName} ${lastName}`.trim());
    }
    identify(identifyEvent);
  }
}
